import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, LogOut, RefreshCw, Building2, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'components/ui/dropdown-menu';
import * as AuthModule from 'modules/auth';
import * as AuthApi from 'modules/auth/api';
import { loadAccessContext } from 'modules/auth/access';
import config from 'config';

export const AwaitingAccessScreen: React.FC = () => {
  const { t } = useTranslation();
  const {
    state: { organization, organizations },
    methods,
  } = AuthModule.Context.useAuthContext();
  const [switching, setSwitching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const orgName = organization?.name || t('organization', { defaultValue: 'Organization' });

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadAccessContext(methods);
      window.location.reload();
    } catch {
      window.location.reload();
    }
  };

  const handleSwitchOrg = async (orgId: string) => {
    if (orgId === organization?.id || switching) return;
    setSwitching(true);
    try {
      const { data } = await AuthApi.SwitchOrganization({ organizationId: orgId });
      methods.setOrganization({
        id: data.organization.id,
        name: data.organization.name,
        slug: data.organization.slug,
      });
      methods.setRole(data.role);
      localStorage.setItem(config.api.active_org_key, data.organization.id);
      await loadAccessContext(methods);
      window.location.href = '/app';
    } catch {
      window.location.reload();
    } finally {
      setSwitching(false);
    }
  };

  const otherOrgs = (organizations || []).filter((o) => o.id !== organization?.id);

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-muted/20 p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="p-8 text-center space-y-6">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <Clock className="h-7 w-7" aria-hidden />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                <Badge variant="warning">
                  {t('role_no_access', { defaultValue: 'No Access' })}
                </Badge>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                {t('awaiting_access_title', { defaultValue: 'Awaiting Access Approval' })}
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('awaiting_access_desc', {
                  defaultValue:
                    'Your account has joined {{org}}, but has no access permissions yet. An administrator must assign you a role before you can access this organization.',
                  org: orgName,
                })}
              </p>
            </div>

            <div className="space-y-3 pt-2">
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                className="w-full"
                size="lg"
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
                />
                {t('check_access_status', { defaultValue: 'Check status' })}
              </Button>

              {otherOrgs.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full" size="lg">
                      <Building2 className="mr-2 h-4 w-4" />
                      {t('switch_organization', { defaultValue: 'Switch organization' })}
                      <ChevronDown className="ml-auto h-4 w-4 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-72">
                    {otherOrgs.map((o) => (
                      <DropdownMenuItem
                        key={o.id}
                        onClick={() => handleSwitchOrg(o.id)}
                        className="cursor-pointer"
                      >
                        <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span className="truncate">{o.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <Button
                variant="ghost"
                onClick={() => methods.logout()}
                className="w-full text-muted-foreground hover:text-foreground"
                size="sm"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('auth.sign_out', { defaultValue: 'Sign out' })}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AwaitingAccessScreen;
